import React, { useState, useEffect, useMemo } from "react";
import { Plus, Trash2, Save, Edit3, Users, X, AlertCircle, Lock, Calendar } from "lucide-react";
import { getAccessList, saveAccessList } from "shared/api/function_data";
import { usePermissions } from "shared/context/PermissionsContext";
import { getAllSidebarItems } from "./AdminConstants";
import { CollapsibleSection, Toast } from "./AdminShared";

function AccessManagementTab({ functionId, functionSidebar, year }) {
  const { canManageUsers } = usePermissions();

  const [accessList, setAccessList] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessToast, setAccessToast] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const sidebarItems = useMemo(() => getAllSidebarItems(functionSidebar), [functionSidebar]);

  const searchTerms = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchQuery.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
  }, [searchQuery]);

  const filteredIndices = useMemo(() => {
    if (searchTerms.length === 0) return accessList.map((_, i) => i);
    return accessList.reduce((acc, user, index) => {
      const login = (user.login || "").toLowerCase();
      const accessTill = (user.access_till || "").toLowerCase();
      if (searchTerms.some((term) => login.includes(term) || accessTill.includes(term))) acc.push(index);
      return acc;
    }, []);
  }, [accessList, searchTerms]);

  useEffect(() => {
    if (!functionId) return;
    setLoadingAccess(true);
    getAccessList(functionId, year)
      .then((data) => setAccessList(data || []))
      .catch(() => setAccessList([]))
      .finally(() => setLoadingAccess(false));
  }, [functionId, year]);

  const handleSaveAccess = async () => {
    setSavingAccess(true);
    try {
      await saveAccessList(functionId, accessList, year);
      setAccessToast({ message: "Access list saved!", type: "success" });
      setEditingIndex(null);
    } catch (err) {
      setAccessToast({ message: err.message, type: "error" });
    } finally {
      setSavingAccess(false);
    }
  };

  const addUser = () => {
    setAccessList((prev) => [{ login: "", access_till: "", role: "viewer", allowed_items: [] }, ...prev]);
    setEditingIndex(0);
    setSearchQuery("");
  };

  const removeUser = (index) => {
    const user = accessList[index];
    if (user.role === "admin") {
      const adminCount = accessList.filter((u) => u.role === "admin").length;
      if (adminCount <= 1) { alert("Cannot remove the last admin."); return; }
    }
    setAccessList((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
    else if (editingIndex !== null && editingIndex > index) setEditingIndex((prev) => prev - 1);
  };

  const updateUser = (index, field, value) => {
    setAccessList((prev) => prev.map((u, i) => {
      if (i !== index) return u;
      const updated = { ...u, [field]: value };
      if (field === "role" && value === "admin") updated.allowed_items = [];
      if (field === "role" && value === "editor" && !updated.allowed_items) updated.allowed_items = [];
      return updated;
    }));
  };

  const toggleAllowedItem = (userIndex, itemKey) => {
    setAccessList((prev) => prev.map((u, i) => {
      if (i !== userIndex) return u;
      const current = u.allowed_items || [];
      const updated = current.includes(itemKey) ? current.filter((k) => k !== itemKey) : [...current, itemKey];
      return { ...u, allowed_items: updated };
    }));
  };

  const selectAllItems = (userIndex) => {
    setAccessList((prev) => prev.map((u, i) => i !== userIndex ? u : { ...u, allowed_items: sidebarItems.map((item) => item.key) }));
  };

  const clearAllItems = (userIndex) => {
    setAccessList((prev) => prev.map((u, i) => i !== userIndex ? u : { ...u, allowed_items: [] }));
  };

  const clearSearch = () => setSearchQuery("");

  // ——— Read-only for non-admins ———
  if (!canManageUsers) {
    return (
      <div className="admin-tab-content">
        <div className="admin-permission-notice">
          <Lock size={20} />
          <div><h4>View Only</h4><p>Contact an admin to manage access.</p></div>
        </div>
        <CollapsibleSection title="User Access" subtitle="Current users">
          <div className="admin-access-search">
            <div className="admin-access-search__input-wrap">
              <Users size={14} className="admin-access-search__icon" />
              <input className="admin-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by login (comma-separated)..." />
              {searchQuery && <button className="admin-btn-icon admin-access-search__clear" onClick={clearSearch}><X size={14} /></button>}
            </div>
            {searchTerms.length > 0 && (
              <div className="admin-access-search__summary">
                Showing {filteredIndices.length} of {accessList.length} user(s)
                {searchTerms.length > 1 && <span className="admin-access-search__tags">{searchTerms.map((term, i) => <span className="admin-access-search__tag" key={i}>{term}</span>)}</span>}
              </div>
            )}
          </div>
          <div className="admin-access-table-wrapper">
            <table className="admin-access-table">
              <thead><tr><th>Login</th><th>Access Till</th><th>Role</th><th>Allowed Items</th></tr></thead>
              <tbody>
                {filteredIndices.map((index) => {
                  const user = accessList[index];
                  return (
                    <tr key={index}>
                      <td>{user.login || "—"}</td>
                      <td>{user.access_till ? new Date(user.access_till).toLocaleDateString() : "—"}</td>
                      <td><span className={`admin-role-badge admin-role-badge--${user.role}`}>{user.role}</span></td>
                      <td>{user.role === "editor" ? (user.allowed_items || []).join(", ") || "None" : user.role === "admin" ? "All" : "Read-only"}</td>
                    </tr>
                  );
                })}
                {filteredIndices.length === 0 && <tr><td colSpan={4} className="admin-access-table__empty">{searchTerms.length > 0 ? "No users match your search" : "No users configured"}</td></tr>}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  if (loadingAccess) return <div className="admin-tab-content"><p>Loading access list...</p></div>;

  // ——— Full editable view ———
  return (
    <div className="admin-tab-content">
      <CollapsibleSection
        title="User Access"
        subtitle={`${accessList.length} user(s) · Changes save to access_config.json`}
        actions={
          <div className="admin-section__action-btns">
            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={addUser}><Plus size={12} /> Add User</button>
            <button className={`admin-btn admin-btn--sm admin-btn--primary ${savingAccess ? "admin-btn--loading" : ""}`} onClick={handleSaveAccess} disabled={savingAccess}>
              {savingAccess ? "Saving..." : <><Save size={12} /> Save Access</>}
            </button>
          </div>
        }
      >
        {/* Search Bar */}
        <div className="admin-access-search">
          <div className="admin-access-search__input-wrap">
            <Users size={14} className="admin-access-search__icon" />
            <input className="admin-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by login (comma-separated for multiple)..." />
            {searchQuery && <button className="admin-btn-icon admin-access-search__clear" onClick={clearSearch}><X size={14} /></button>}
          </div>
          {searchTerms.length > 0 && (
            <div className="admin-access-search__summary">
              <span>Showing <strong>{filteredIndices.length}</strong> of <strong>{accessList.length}</strong> user(s)</span>
              <span className="admin-access-search__tags">
                {searchTerms.map((term, i) => (
                  <span className="admin-access-search__tag" key={i}>
                    {term}
                    <button className="admin-access-search__tag-remove" onClick={() => { const updated = searchTerms.filter((_, idx) => idx !== i).join(", "); setSearchQuery(updated); }}><X size={10} /></button>
                  </span>
                ))}
                <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={clearSearch}>Clear All</button>
              </span>
            </div>
          )}
        </div>

        {/* User List */}
        {filteredIndices.map((index) => {
          const user = accessList[index];
          const isEditing = editingIndex === index;
          const isEditor = user.role === "editor";
          const allowedCount = (user.allowed_items || []).length;

          return (
            <div className={`admin-access-user ${isEditing ? "admin-access-user--editing" : ""} ${index === 0 && !searchQuery ? "admin-access-user--new" : ""}`} key={index}>
              <div className="admin-access-user__row">
                <div className="admin-access-user__fields">
                  <input className="admin-input admin-input--table" value={user.login} onChange={(e) => updateUser(index, "login", e.target.value)} placeholder="login" autoFocus={index === 0 && !user.login} />
                  <input className="admin-input admin-input--table" type="date" value={user.access_till || ""} onChange={(e) => updateUser(index, "access_till", e.target.value)} title="Access granted till this date" />
                  <select className="admin-input admin-select admin-input--table" value={user.role} onChange={(e) => updateUser(index, "role", e.target.value)}>
                    <option value="admin">Admin</option>
                    <option value="editor">Contributer</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  {isEditor && <span className="admin-access-user__item-count">{allowedCount}/{sidebarItems.length} items</span>}
                </div>
                <div className="admin-access-user__actions">
                  {isEditor && <button className="admin-btn-icon" onClick={() => setEditingIndex(isEditing ? null : index)} title="Edit allowed items"><Edit3 size={13} /></button>}
                  <button className="admin-btn-icon admin-btn-icon--danger" onClick={() => removeUser(index)}><Trash2 size={13} /></button>
                </div>
              </div>

              {isEditing && isEditor && (
                <div className="admin-access-user__items">
                  <div className="admin-access-user__items-header">
                    <span className="admin-access-user__items-title">Allowed Sidebar Items ({allowedCount} selected)</span>
                    <div className="admin-access-user__items-actions">
                      <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => selectAllItems(index)}>Select All</button>
                      <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => clearAllItems(index)}>Clear All</button>
                    </div>
                  </div>
                  <div className="admin-access-user__items-grid">
                    {sidebarItems.map((item) => {
                      const checked = (user.allowed_items || []).includes(item.key);
                      return (
                        <label className={`admin-access-item-checkbox ${checked ? "admin-access-item-checkbox--checked" : ""}`} key={item.key}>
                          <input type="checkbox" checked={checked} onChange={() => toggleAllowedItem(index, item.key)} />
                          <span className="admin-access-item-checkbox__label">{item.label}</span>
                          <span className="admin-access-item-checkbox__meta">{item.type}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="admin-field__hint">Editor can only edit and upload data for selected items.</p>
                </div>
              )}
            </div>
          );
        })}

        {filteredIndices.length === 0 && searchTerms.length > 0 && (
          <div className="admin-empty-state admin-empty-state--sm">
            <Users size={24} /><p>No users match "<strong>{searchQuery}</strong>"</p>
            <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={clearSearch}>Clear Search</button>
          </div>
        )}
        {accessList.length === 0 && (
          <div className="admin-empty-state admin-empty-state--sm">
            <p>No users configured</p>
            <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={addUser}><Plus size={12} /> Add User</button>
          </div>
        )}
      </CollapsibleSection>

      {/* Role Reference */}
      <CollapsibleSection title="Role Reference" defaultOpen={false}>
        <div className="admin-permissions-grid">
          <div className="admin-permission-card">
            <div className="admin-permission-card__badge admin-permission-card__badge--admin">Admin</div>
            <ul className="admin-permission-card__list"><li>Full access to everything</li><li>Manage users</li><li>Upload data for all items</li><li>See Admin Panel in navbar</li></ul>
          </div>
          <div className="admin-permission-card">
            <div className="admin-permission-card__badge admin-permission-card__badge--editor">Editor</div>
            <ul className="admin-permission-card__list"><li>Edit & upload for allowed items only</li><li>See Upload button in navbar</li><li>Cannot manage users</li><li>Cannot access admin panel</li></ul>
          </div>
          <div className="admin-permission-card">
            <div className="admin-permission-card__badge admin-permission-card__badge--viewer">Viewer</div>
            <ul className="admin-permission-card__list"><li>View all data (read-only)</li><li>No upload or edit</li><li>No admin access</li></ul>
          </div>
        </div>
      </CollapsibleSection>

      {accessToast && <Toast message={accessToast.message} type={accessToast.type} onClose={() => setAccessToast(null)} />}
    </div>
  );
}

export default AccessManagementTab;
